import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import Imgs from "@/views/components/Upload/Imgs";
import Video from "@/views/components/Upload/Video";
import WangEditor from "@/views/components/WangEditor";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { Form, Input, Spin, Typography } from "antd";
import { useMemo, useState } from "react";

import {
    Button as ZhButton,
    Checkbox as ZhCheckbox,
    Dialog as ZhDialog,
    Image as ZhImage,
    InputNumber as ZhInputNumber,
    RadioGroup as ZhRadioGroup,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import type { ActivityRelatedProductItem } from "./types";
import "./ActivityRelatedProductSelector.scss";

const productTypeLabels: Record<number, string> = {
    1: "寄售商品",
    2: "团购商品",
    3: "捡漏商品",
    4: "活动商品"
};

export interface ActivityRelatedProductCandidate extends ActivityRelatedProductItem {
    mainImgUrl?: string;
    productCode?: string;
    addTime?: string;
    product?: {
        title?: string;
        productTypeName?: string;
        supplierName?: string;
        supplierEnterprise?: { name?: string; enterpriseName?: string; supplierName?: string };
        [key: string]: unknown;
    };
}

export interface ActivityRelatedProductSelectorResult {
    list?: ActivityRelatedProductCandidate[];
    total?: number;
}

export interface ActivityRelatedProductSelectorProps {
    /** 编辑页与 Vue 一致额外展示上架状态和添加时间。 */
    isEdit?: boolean;
    value?: ActivityRelatedProductItem[] | string;
    onChange?: (value: ActivityRelatedProductItem[] | string) => void;
    loadCandidates?: (query: {
        page: number;
        size: number;
        keyword?: string;
        productTypeName?: string;
        supplierName?: string;
        type?: number;
        excludeProducts?: Array<{ productId: string; type: number }>;
    }) => Promise<ActivityRelatedProductSelectorResult>;
}

interface ActivityProductFormValues {
    supplierId?: number | string;
    name?: string;
    stock?: number;
    pricingMethod?: number;
    marketPrice?: number;
    estimatedUnitPrice?: number;
    lowerThanMarketPrice?: number;
    hasOverbooking?: number;
    minOrderTon?: number;
    minAdjustmentTon?: number;
    orderRule?: number;
    intentionDeposit?: number;
    tonStep?: number;
    amountStep?: number;
    detail?: string;
    pictureFileIds?: Array<number | string>;
    videoFileId?: number | string;
}

interface SupplierOption {
    label: string;
    value: number | string;
    supplierEnterprise?: Record<string, unknown>;
}

/** 复用 Vue 商品选择器接口，保留活动表单的商品类型和排除项契约。 */
export async function loadActivityRelatedProductCandidates(query: {
    page: number;
    size: number;
    keyword?: string;
    productTypeName?: string;
    supplierName?: string;
    type?: number;
    excludeProducts?: Array<{ productId: string; type: number }>;
}): Promise<ActivityRelatedProductSelectorResult> {
    const response = await request.service.post<ActivityRelatedProductSelectorResult>(
        "/admin/product/campaign-paging-selector",
        query
    );
    return response.data;
}

/** 读取关联商品在列表、编辑回显和新增活动商品中的稳定引用。 */
const itemReference = (item: ActivityRelatedProductItem) =>
    item.id ??
    item.productId ??
    item.productCampaign?.code ??
    item.productCampaign?.id ??
    item.product?.code ??
    item.product?.id ??
    item.clientId;

const itemKey = (item: ActivityRelatedProductItem) => {
    const reference = itemReference(item);
    return item.type == null || reference == null ? "" : `${Number(item.type)}:${String(reference)}`;
};

/** 保留后端商品编号的原始字符串，避免超出 Number.MAX_SAFE_INTEGER 后发生精度丢失。 */
const normalizeItems = (value?: ActivityRelatedProductItem[] | string): ActivityRelatedProductItem[] => {
    const items = Array.isArray(value)
        ? value
        : String(value || "")
              .split(",")
              .map(item => item.trim().split(":"))
              .filter(([type, id]) => type && id)
              .map(([type, id]) => ({ type: Number(type), id }));
    const seen = new Set<string>();
    return items.filter(item => {
        const key = itemKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/** 兼容 Vue 商品候选接口的嵌套企业名称和扁平 supplierName 返回结构。 */
const getCandidateSupplierName = (candidate: ActivityRelatedProductCandidate) =>
    candidate.product?.supplierEnterprise?.name ||
    candidate.product?.supplierEnterprise?.enterpriseName ||
    candidate.product?.supplierEnterprise?.supplierName ||
    candidate.product?.supplierName ||
    "-";

const getItemTitle = (item: ActivityRelatedProductItem) =>
    String(item.type) === "4"
        ? String(item.productCampaign?.name || item.product?.name || item.product?.title || "-")
        : String(item.product?.title || "-");

type ActivityRelatedProductFilterState = {
    productTypeName: string;
    supplierName: string;
    candidateType: string;
};

/** 活动商品详情编辑器，维持 Ant Form 的受控字段约定。 */
function ActivityProductEditor({ value, onChange }: { value?: string; onChange?: (value: string) => void }) {
    return <WangEditor height="120px" placeholder="请输入商品详情" value={value} onUpdateValue={onChange} />;
}

/** 严格复刻 Vue ProductList 与 AddProductDialog 的商品列表、选择弹窗和新增活动商品分支。 */
export function ActivityRelatedProductSelector({
    isEdit = false,
    value,
    onChange,
    loadCandidates = loadActivityRelatedProductCandidates
}: ActivityRelatedProductSelectorProps) {
    const [activityProductForm] = Form.useForm<ActivityProductFormValues>();
    const [open, setOpen] = useState(false);
    const [dialogType, setDialogType] = useState(1);
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectorError, setSelectorError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<ActivityRelatedProductCandidate[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [productTypeName, setProductTypeName] = useState("");
    const [supplierName, setSupplierName] = useState("");
    const [candidateType, setCandidateType] = useState("");
    const [pendingKeys, setPendingKeys] = useState<string[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
    const [pictureFiles, setPictureFiles] = useState<UploadUserFile[]>([]);
    const [mainImgUrl, setMainImgUrl] = useState("");
    const selectedItems = useMemo(() => normalizeItems(value), [value]);
    const pricingMethod = Form.useWatch("pricingMethod", activityProductForm);
    const orderRule = Form.useWatch("orderRule", activityProductForm);

    const queryCandidates = async (
        nextKeyword = keyword,
        nextPage = 1,
        filterOverrides?: Partial<ActivityRelatedProductFilterState>
    ) => {
        const filters: ActivityRelatedProductFilterState = {
            productTypeName,
            supplierName,
            candidateType,
            ...filterOverrides
        };
        setLoading(true);
        setSelectorError(null);
        setPage(nextPage);
        try {
            // Vue 只把寄售、团购、捡漏商品作为已关联排除项，活动商品由页面本地维护。
            const excludeProducts = selectedItems
                .filter(item => [1, 2, 3].includes(Number(item.type)) && itemReference(item) != null)
                .map(item => ({ productId: String(itemReference(item)), type: Number(item.type) }));
            const result = await loadCandidates({
                page: nextPage,
                size: 10,
                ...(nextKeyword.trim() ? { keyword: nextKeyword.trim() } : {}),
                ...(filters.productTypeName.trim() ? { productTypeName: filters.productTypeName.trim() } : {}),
                ...(filters.supplierName.trim() ? { supplierName: filters.supplierName.trim() } : {}),
                ...(filters.candidateType ? { type: Number(filters.candidateType) } : {}),
                ...(excludeProducts.length ? { excludeProducts } : {})
            });
            setCandidates(result.list || []);
            setTotal(result.total || 0);
        } catch {
            setSelectorError("活动商品候选加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 打开新增商品弹窗时恢复 Vue 的默认“关联已有商品”状态。 */
    const openDialog = () => {
        setOpen(true);
        setDialogType(1);
        setPendingKeys([]);
        setSelectorError(null);
        activityProductForm.resetFields();
        setPictureFiles([]);
        setMainImgUrl("");
        void queryCandidates("", 1);
    };

    const removeItem = (index: number) => onChange?.(selectedItems.filter((_item, itemIndex) => itemIndex !== index));

    /** 旧版活动商品供应商下拉远程搜索，同时保留构造 supplierEnterprise 所需的原始资料。 */
    const querySuppliers = async (searchKeyword = "") => {
        try {
            const suppliers = await enterpriseManageService.selector({ supplierName: searchKeyword || undefined });
            setSupplierOptions(
                suppliers.map(supplier => ({
                    label: supplier.supplierName || "-",
                    value: supplier.id,
                    supplierEnterprise: {
                        code: supplier.code,
                        name: supplier.supplierName,
                        unifiedSocialCreditCode: supplier.socialCreditCode,
                        legalRepresentative: supplier.legalRepresentative,
                        realName: supplier.contactPerson,
                        mobile: supplier.contactPhone
                    }
                }))
            );
        } catch {
            // 供应商候选失败不应中断弹窗交互，用户仍可重试远程搜索。
            setSupplierOptions([]);
        }
    };

    /** 弹窗提交按旧版分支合并已有商品，或构造新增活动商品的 product 结构。 */
    const submitDialog = async () => {
        if (dialogType === 1) {
            const additions = candidates.filter(candidate => pendingKeys.includes(itemKey(candidate)));
            onChange?.(normalizeItems([...selectedItems, ...additions]));
            setOpen(false);
            return;
        }
        const values = await activityProductForm.validateFields();
        const supplier = supplierOptions.find(option => String(option.value) === String(values.supplierId));
        const activityProduct: ActivityRelatedProductItem = {
            clientId: `new-${Date.now()}`,
            type: 4,
            mainImgUrl,
            product: {
                ...values,
                title: values.name,
                supplierEnterprise: supplier?.supplierEnterprise
            }
        };
        onChange?.([...selectedItems, activityProduct]);
        setOpen(false);
    };

    const selectedColumns: TableColumnProps<ActivityRelatedProductItem>[] = [
        { key: "index", label: "序号", width: 68, prop: "index", render: ({ rowIndex }) => rowIndex + 1 },
        {
            key: "productId",
            label: "商品编号",
            width: 176,
            prop: "productId",
            render: ({ row }) => String(itemReference(row) || "-")
        },
        {
            key: "title",
            label: "主图/标题",
            width: 550,
            prop: "title",
            render: ({ row }) => (
                <div className="activity-product-list__title-cell">
                    {row.mainImgUrl ? (
                        <ZhImage preview src={row.mainImgUrl} />
                    ) : (
                        <span className="empty-img">暂无图片</span>
                    )}
                    <span className="ellipsis">{getItemTitle(row)}</span>
                </div>
            )
        },
        {
            key: "productTypeName",
            label: "品类",
            width: 120,
            prop: "productTypeName",
            render: ({ row }) => String(row.product?.productTypeName || "-")
        },
        {
            key: "supplierName",
            label: "供应商",
            width: 462,
            prop: "supplierName",
            render: ({ row }) => getCandidateSupplierName(row as ActivityRelatedProductCandidate)
        },
        {
            key: "type",
            label: "商品类型",
            width: 100,
            prop: "type",
            render: ({ row }) => productTypeLabels[Number(row.type)] || "-"
        },
        ...(isEdit
            ? [
                  {
                      key: "state",
                      label: "上架状态",
                      width: 150,
                      prop: "state",
                      render: ({ row }: { row: ActivityRelatedProductItem }) =>
                          Number(row.type) === 4 ? "上架" : Number(row.product?.state) === 1 ? "上架" : "下架"
                  },
                  {
                      key: "addTime",
                      label: "添加时间",
                      width: 150,
                      prop: "addTime",
                      render: ({ row }: { row: ActivityRelatedProductItem }) =>
                          row.addTime ? String(row.addTime).replace("T", " ").slice(0, 19) : "-"
                  }
              ]
            : []),
        {
            key: "operation",
            label: "操作",
            width: 230,
            prop: "operation",
            render: ({ rowIndex }) => (
                <ZhButton type="primary" variant="text" onClick={() => removeItem(rowIndex)}>
                    删除
                </ZhButton>
            )
        }
    ];

    const candidateColumns: TableColumnProps<ActivityRelatedProductCandidate>[] = [
        {
            key: "selection",
            label: "",
            width: 64,
            prop: "selection",
            render: ({ row }) => {
                const key = itemKey(row);
                const title = getItemTitle(row);
                return (
                    <ZhCheckbox
                        aria-label={`选择${title}`}
                        checked={pendingKeys.includes(key)}
                        onChange={checked =>
                            setPendingKeys(current =>
                                checked ? [...current, key] : current.filter(currentKey => currentKey !== key)
                            )
                        }
                    />
                );
            }
        },
        {
            key: "productId",
            label: "商品编号",
            width: 176,
            prop: "productId",
            render: ({ row }) => String(row.productId || "-")
        },
        {
            key: "title",
            label: "商品名称",
            width: 350,
            prop: "title",
            render: ({ row }) => row.product?.title || "-"
        },
        {
            key: "productTypeName",
            label: "品类",
            width: 150,
            prop: "productTypeName",
            render: ({ row }) => row.product?.productTypeName || "-"
        },
        {
            key: "supplierName",
            label: "供应商",
            width: 271,
            prop: "supplierName",
            render: ({ row }) => getCandidateSupplierName(row)
        },
        {
            key: "type",
            label: "商品类型",
            width: 149,
            prop: "type",
            render: ({ row }) => productTypeLabels[Number(row.type)] || "-"
        }
    ];

    return (
        <div className="activity-product-list">
            <ZhButton type="primary" onClick={openDialog}>
                新增商品
            </ZhButton>
            <ZhTable<ActivityRelatedProductItem>
                className="activity-product-list__table"
                columns={selectedColumns}
                data={selectedItems}
                fit={false}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                pagination={false}
                rowKey={item => itemKey(item)}
            />
            <ZhDialog
                alignCenter
                className="activity-product-dialog-modal"
                footer={
                    <div className="activity-product-dialog__footer">
                        <ZhButton onClick={() => setOpen(false)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={() => void submitDialog()}>
                            提交
                        </ZhButton>
                    </div>
                }
                open={open}
                title="新增商品"
                width={1200}
                zIndex={1000}
                onCancel={() => setOpen(false)}
            >
                <div className="activity-product-dialog">
                    <div className="activity-product-dialog__type-row">
                        <span className="required-label">商品类型</span>
                        <ZhRadioGroup
                            options={[
                                { label: "关联寄售/团购/捡漏商品", value: 1 },
                                { label: "新增活动商品", value: 2 }
                            ]}
                            value={dialogType}
                            onChange={event => {
                                const nextType = Number(event.target.value);
                                setDialogType(nextType);
                                if (nextType === 1 && !candidates.length) void queryCandidates("", 1);
                                if (nextType === 2) void querySuppliers();
                            }}
                        />
                    </div>

                    {dialogType === 1 ? (
                        <div className="activity-product-dialog__list-card">
                            <div className="activity-product-dialog__subtitle">商品列表</div>
                            <Spin spinning={loading}>
                                <ZhTable<ActivityRelatedProductCandidate>
                                    data={candidates}
                                    columns={candidateColumns}
                                    fit={false}
                                    pagination={false}
                                    rowKey={candidate => itemKey(candidate)}
                                    isShowSearchBtn={false}
                                    isShowResetBtn={false}
                                    tableHeader={
                                        <div className="activity-product-dialog__filters">
                                            <Input
                                                aria-label="商品搜索"
                                                className="activity-product-dialog__filter-keyword"
                                                placeholder="请输入商品名称/商品编号"
                                                value={keyword}
                                                onChange={event => setKeyword(event.target.value)}
                                            />
                                            <Input
                                                aria-label="候选商品品类"
                                                className="activity-product-dialog__filter-category"
                                                placeholder="请输入品类"
                                                value={productTypeName}
                                                onChange={event => setProductTypeName(event.target.value)}
                                            />
                                            <Input
                                                aria-label="候选商品供应商"
                                                className="activity-product-dialog__filter-supplier"
                                                placeholder="请输入供应商名称"
                                                value={supplierName}
                                                onChange={event => setSupplierName(event.target.value)}
                                            />
                                            <ZhSelect
                                                allowClear
                                                aria-label="候选商品类型"
                                                className="activity-product-dialog__filter-type"
                                                options={[1, 2, 3].map(type => ({
                                                    label: productTypeLabels[type],
                                                    value: String(type)
                                                }))}
                                                placeholder="请选择商品类型"
                                                value={candidateType || undefined}
                                                onChange={value => setCandidateType(String(value || ""))}
                                            />
                                            <ZhButton type="primary" onClick={() => void queryCandidates(keyword, 1)}>
                                                搜索
                                            </ZhButton>
                                            <ZhButton
                                                aria-label="重置商品筛选"
                                                className="activity-product-dialog__filter-reset"
                                                icon={<TableSearchIcon name="reset" />}
                                                onClick={() => {
                                                    setKeyword("");
                                                    setProductTypeName("");
                                                    setSupplierName("");
                                                    setCandidateType("");
                                                    void queryCandidates("", 1, {
                                                        productTypeName: "",
                                                        supplierName: "",
                                                        candidateType: ""
                                                    });
                                                }}
                                            />
                                            {selectorError ? (
                                                <Typography.Text type="danger" role="alert">
                                                    {selectorError}
                                                </Typography.Text>
                                            ) : null}
                                        </div>
                                    }
                                    append={
                                        <div className="activity-product-dialog__pagination">
                                            <ZhPagination
                                                current={page}
                                                pageSize={10}
                                                total={total}
                                                onChange={nextPage => void queryCandidates(keyword, nextPage)}
                                            />
                                        </div>
                                    }
                                />
                            </Spin>
                        </div>
                    ) : (
                        <Form
                            className="activity-product-dialog__activity-form"
                            form={activityProductForm}
                            labelCol={{ flex: "108px" }}
                            wrapperCol={{ flex: 1 }}
                        >
                            <div className="activity-product-dialog__form-grid">
                                <Form.Item
                                    label="供应商"
                                    name="supplierId"
                                    required
                                    rules={[{ required: true, message: "请选择供应商" }]}
                                >
                                    <ZhSelect
                                        allowClear
                                        showSearch
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        onSearch={keyword => void querySuppliers(keyword)}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="活动商品"
                                    name="name"
                                    required
                                    rules={[{ required: true, message: "请输入商品名称" }]}
                                >
                                    <Input placeholder="请输入活动商品" />
                                </Form.Item>
                                <Form.Item
                                    label="库存量"
                                    name="stock"
                                    required
                                    rules={[{ required: true, message: "请输入库存量" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入库存量" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    label="定价方式"
                                    name="pricingMethod"
                                    required
                                    rules={[{ required: true, message: "请选择定价方式" }]}
                                >
                                    <ZhSelect
                                        allowClear
                                        options={[
                                            { label: "市场价+活动单价", value: 1 },
                                            { label: "比市场价直降", value: 2 },
                                            { label: "以活动现场公布为准", value: 3 }
                                        ]}
                                        placeholder="请选择定价方式"
                                    />
                                </Form.Item>
                                {Number(pricingMethod) === 1 ? (
                                    <>
                                        <Form.Item
                                            label="市场价"
                                            name="marketPrice"
                                            required
                                            rules={[{ required: true, message: "请输入市场价" }]}
                                        >
                                            <ZhInputNumber min={0.01} placeholder="请输入市场价" suffix="元/吨" />
                                        </Form.Item>
                                        <Form.Item
                                            label="活动单价"
                                            name="estimatedUnitPrice"
                                            required
                                            rules={[{ required: true, message: "请输入活动单价" }]}
                                        >
                                            <ZhInputNumber min={0.01} placeholder="请输入活动单价" suffix="元/吨" />
                                        </Form.Item>
                                    </>
                                ) : null}
                                {Number(pricingMethod) === 2 ? (
                                    <Form.Item
                                        label="比市场价直降"
                                        name="lowerThanMarketPrice"
                                        required
                                        rules={[{ required: true, message: "请输入直降单价" }]}
                                    >
                                        <ZhInputNumber min={0.01} placeholder="请输入直降单价" suffix="元/吨" />
                                    </Form.Item>
                                ) : null}
                                <Form.Item
                                    label="是否允许超售"
                                    name="hasOverbooking"
                                    required
                                    rules={[{ required: true, message: "请选择是否允许超售" }]}
                                >
                                    <ZhRadioGroup
                                        options={[
                                            { label: "是", value: 1 },
                                            { label: "否", value: 0 }
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="起订吨数"
                                    name="minOrderTon"
                                    required
                                    rules={[{ required: true, message: "请输入起订吨数" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入起订吨数" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    label="最小调整吨数"
                                    name="minAdjustmentTon"
                                    required
                                    rules={[{ required: true, message: "请输入最小调整吨数" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入最小调整吨数" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    className="activity-product-dialog__form-full"
                                    label="下单要求"
                                    name="orderRule"
                                    required
                                    rules={[{ required: true, message: "请选择下单要求" }]}
                                >
                                    <ZhRadioGroup
                                        options={[
                                            { label: "固定金额", value: 1 },
                                            { label: "阶梯金额", value: 2 }
                                        ]}
                                    />
                                </Form.Item>
                                {Number(orderRule) === 1 ? (
                                    <Form.Item
                                        label="意向金"
                                        name="intentionDeposit"
                                        required
                                        rules={[{ required: true, message: "请输入意向金" }]}
                                    >
                                        <ZhInputNumber min={1} placeholder="请输入意向金" suffix="元" />
                                    </Form.Item>
                                ) : null}
                                {Number(orderRule) === 2 ? (
                                    <>
                                        <Form.Item
                                            label="最低意向金"
                                            name="intentionDeposit"
                                            required
                                            rules={[{ required: true, message: "请输入意向金" }]}
                                        >
                                            <ZhInputNumber min={0} placeholder="请输入意向金" suffix="元" />
                                        </Form.Item>
                                        <Form.Item
                                            label="每增加"
                                            name="tonStep"
                                            required
                                            rules={[{ required: true, message: "请输入吨数" }]}
                                        >
                                            <ZhInputNumber min={1} placeholder="请输入吨数" suffix="吨" />
                                        </Form.Item>
                                        <Form.Item
                                            label="增加"
                                            name="amountStep"
                                            required
                                            rules={[{ required: true, message: "请输入金额" }]}
                                        >
                                            <ZhInputNumber min={1} placeholder="请输入金额" suffix="元" />
                                        </Form.Item>
                                    </>
                                ) : null}
                                <Form.Item
                                    className="activity-product-dialog__form-full"
                                    label="商品详情"
                                    name="detail"
                                    required
                                    rules={[{ required: true, message: "请输入商品详情" }]}
                                >
                                    <ActivityProductEditor />
                                </Form.Item>
                                <Form.Item className="activity-product-dialog__form-full" label="图片" required>
                                    <Imgs
                                        fileList={pictureFiles}
                                        fileSize={10}
                                        fileType={["image/jpeg", "image/png", "image/webp", "image/avif"]}
                                        limit={6}
                                        onUpdateData={file => {
                                            const current = activityProductForm.getFieldValue("pictureFileIds") || [];
                                            activityProductForm.setFieldValue("pictureFileIds", [...current, file.id]);
                                            if (!mainImgUrl) setMainImgUrl(file.path || "");
                                        }}
                                        onUpdateFileList={setPictureFiles}
                                    />
                                    <div className="activity-product-dialog__upload-hint">
                                        支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，请勿包含敏感信息或侵权内容
                                    </div>
                                </Form.Item>
                                <Form.Item className="activity-product-dialog__form-full" label="视频">
                                    <Video
                                        fileSize={200}
                                        fileType={["video/mp4", "video/quicktime"]}
                                        onUpdateData={file => activityProductForm.setFieldValue("videoFileId", file.id)}
                                    />
                                    <div className="activity-product-dialog__upload-hint">
                                        仅支持mov、mp4格式，大小不超过200M
                                    </div>
                                </Form.Item>
                            </div>
                        </Form>
                    )}
                </div>
            </ZhDialog>
        </div>
    );
}

export default ActivityRelatedProductSelector;
